import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import Hub from '../components/Hub';
import { HomeCardsSkeleton } from '../components/Skeletons';
import Ring from '../components/Ring';
import Icon from '../components/Icon';
import DayTypeChip from '../components/DayTypeChip';
import { useCapture, useCapturedDay } from '../components/Capture';
import { greeting, longDate, today, weekDays } from '../lib/dates';
import { MEAL_LABELS, MEAL_TYPES, grams, kcal, mealTypeForNow } from '../lib/format';
import { calorieSummary, isLogged, mascotMood, nextAction, proteinSummary } from '../lib/day';
import { logPlannedMeal, plannedMeal } from '../lib/plans';
import { useApp, useAppPath } from '../lib/AppContext';
import { AnnouncementBanner } from '../components/Announcements';

const CARD = 'rounded-[20px] border border-base-300 bg-base-100 p-5';

function useHomeData() {
  const { api } = useApp();
  const todayIso = today();
  const [day, setDay] = useState(null);
  // null / undefined mean "not loaded yet", so the hub shows "…" instead of a zero that looks real.
  const [logged, setLogged] = useState(null);
  const [plan, setPlan] = useState(undefined);

  useEffect(() => {
    api.get(`/days/${todayIso}`).then(setDay).catch(() => {});
    const days = weekDays(todayIso);
    api.get(`/stats?${new URLSearchParams({ from: days[0], to: todayIso, group: 'day' })}`)
      .then((data) => setLogged(Object.fromEntries(data.series.map((r) => [r.period, r.logged]))))
      .catch(() => setLogged({}));
    api.get(`/meal_plans/current?date=${todayIso}`).then(setPlan).catch(() => setPlan(null));
  }, [api, todayIso]);

  // A save from the capture button refreshes today in place; the week follows from it.
  useCapturedDay(todayIso, setDay);
  const todayLogged = day ? isLogged(day) : !!logged?.[todayIso];
  const week = weekDays(todayIso).map((iso) => ({
    iso, today: iso === todayIso, future: iso > todayIso, logged: iso === todayIso ? todayLogged : !!logged?.[iso],
  }));
  return { day, week, weekLoaded: logged != null, plan };
}

// The next step, with the dish this week's menu plans for that meal when there is one.
function NextCard({ action, planned }) {
  const { api } = useApp();
  const capture = useCapture();
  const path = useAppPath();
  const [busy, setBusy] = useState(false);
  const { calories, protein } = action;
  const details = [
    calories.goal != null && (calories.over ? `Vượt ${kcal(-calories.left)} kcal` : `Còn ${kcal(calories.left)} kcal`),
    protein.missing > 0 && `thiếu ${grams(protein.missing)} đạm`,
  ].filter(Boolean).join(' · ');
  const meal = action.kind === 'meal' ? MEAL_LABELS[action.mealType].toLowerCase() : null;

  const logPlanned = async () => {
    setBusy(true);
    try {
      capture.saved(await logPlannedMeal(api, today(), planned));
    } catch (e) {
      window.alert(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className={`${CARD} flex flex-col gap-3`}>
      <div className="flex items-center gap-4">
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted">Tiếp theo</div>
          <h2 className="text-lg font-bold leading-snug">{action.title}</h2>
          {action.kind === 'night' && <p className="text-sm text-muted">Ngày mới bắt đầu khi bạn thức dậy, bữa sáng tính sau nhé.</p>}
          {action.kind === 'done' && <p className="text-sm text-muted">Đã đủ đạm cho hôm nay.</p>}
          {(action.kind === 'meal' || action.kind === 'protein') && details && <p className="text-sm text-muted">{details}</p>}
        </div>
        {meal && capture.enabled && !planned && (
          <button type="button" className="btn btn-primary h-11 shrink-0 rounded-[14px] border-0 px-4"
            onClick={() => capture.open('meal', { date: today(), mealType: action.mealType })}>
            <Icon name="camera" className="size-5" /> Chụp {meal}
          </button>
        )}
        {action.kind === 'protein' && (
          <Link to={path('/plan')} className="btn btn-primary h-11 shrink-0 rounded-[14px] border-0 px-4">Xem gợi ý</Link>
        )}
      </div>
      {meal && planned && (
        <div className="flex flex-col gap-3 rounded-[14px] bg-base-200 p-3">
          <div>
            <div className="text-xs text-muted">Theo thực đơn tuần</div>
            <div className="font-semibold">{planned.name}</div>
            <div className="tabular text-sm text-muted">{kcal(planned.calories)} kcal · {grams(planned.protein_g)} đạm</div>
          </div>
          {capture.enabled && (
            <div className="flex gap-2">
              <button type="button" className="btn btn-primary h-11 flex-1 rounded-[14px] border-0" disabled={busy} onClick={logPlanned}>
                {busy ? <span className="loading loading-spinner loading-xs" /> : <Icon name="check" className="size-5" />} Ghi món này
              </button>
              <button type="button" className="btn h-11 rounded-[14px] border-base-300 bg-base-100 px-4" aria-label={`Chụp ${meal}`}
                onClick={() => capture.open('meal', { date: today(), mealType: action.mealType })}>
                <Icon name="camera" className="size-5" />
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function TodayCard({ day, goals }) {
  const path = useAppPath();
  const c = calorieSummary(day.totals, day.calorie_goal ?? goals.calories);
  const p = proteinSummary(day.totals, day.protein_goal);

  return (
    <section className={CARD}>
      <div className="mb-4 flex items-center justify-between gap-2">
        <h2 className="text-lg font-bold">Hôm nay</h2>
        <DayTypeChip workoutDay={day.workout_day} />
      </div>
      <div className="flex items-center gap-4">
        <div className="flex flex-col items-center gap-1">
          <Ring pct={c.pct} size={64} stroke={10} colorClass={c.over ? 'text-error' : 'text-brand-text'}
            label={`Hôm nay: đã ăn ${kcal(c.eaten)}${c.goal ? ` trên ${kcal(c.goal)}` : ''} kcal`}>
            <span className="tabular text-sm font-extrabold">{kcal(c.eaten)}</span>
          </Ring>
          <span className="text-xs font-semibold">Calo</span>
          {c.goal && <span className="tabular text-[11px] text-muted">/ {kcal(c.goal)}</span>}
        </div>
        <div className="flex flex-col items-center gap-1">
          <Ring pct={p.pct} size={64} stroke={10} colorClass="text-protein"
            label={`Đạm: ${grams(p.value)}${p.goal ? ` trên ${grams(p.goal)}` : ''}`}>
            <span className="tabular text-sm font-extrabold">{Math.round(p.value)}</span>
          </Ring>
          <span className="text-xs font-semibold">Đạm</span>
          {p.goal && <span className="tabular text-[11px] text-muted">/ {grams(p.goal)}</span>}
        </div>
        <dl className="tabular flex flex-1 flex-col gap-1.5 border-l border-base-300 pl-4 text-sm">
          <dt className="text-xs font-semibold text-muted">Chỉ theo dõi</dt>
          <div className="flex justify-between"><dt>Tinh bột</dt><dd className="font-bold">{grams(day.totals.carbs_g)}</dd></div>
          <div className="flex justify-between"><dt>Chất béo</dt><dd className="font-bold">{grams(day.totals.fat_g)}</dd></div>
        </dl>
      </div>
      <Link to={path('/day')} className="mt-4 flex min-h-11 items-center justify-center gap-1 text-[15px] font-semibold text-brand-text">
        Xem chi tiết hôm nay <Icon name="chevronRight" className="size-4" />
      </Link>
    </section>
  );
}

// Home is for a quick glance and the next action; Today is for detail and editing.
export default function HomePage() {
  const { goals } = useApp();
  const capture = useCapture();
  const { day, week, weekLoaded, plan } = useHomeData();
  const action = day ? nextAction(day, goals) : null;
  const eaten = day?.totals.calories_in ?? 0;
  const daysSoFar = week.filter((d) => !d.future);
  const loggedSoFar = daysSoFar.filter((d) => d.logged).length;
  const mealDue = action?.kind === 'meal';
  // The menu's dish for the meal due now, else for the next slot not logged yet today.
  const nowIndex = MEAL_TYPES.indexOf(mealTypeForNow());
  const nextSlot = MEAL_TYPES.slice(nowIndex).find((t) => t !== 'snack' && !day?.meals.some((m) => m.meal_type === t)) || 'breakfast';
  const suggestFor = MEAL_LABELS[nextSlot].toLowerCase();
  const nextPlanned = plannedMeal(plan, today(), nextSlot);
  const duePlanned = mealDue ? plannedMeal(plan, today(), action.mealType) : null;

  // The mascot says what matters now: the protein still missing, what to eat next, or a cheer.
  const NEXT_MEAL_WORD = { breakfast: 'Sáng', lunch: 'Trưa', dinner: 'Tối' };
  const mood = day ? mascotMood(day) : 'ok';
  const missing = day ? proteinSummary(day.totals, day.protein_goal).missing : null;
  const mascot = !day ? { mood } : mood === 'done'
    ? { mood, text: 'Đủ đạm rồi, ngon quá!', spoken: 'Bạn Mate: đã đủ đạm hôm nay. Mở chi tiết hôm nay', to: '/day' }
    : mood === 'hungry'
      ? { mood, text: `Còn ${missing} g đạm nè!`, spoken: `Bạn Mate: còn thiếu ${missing} gam đạm. Ghi bữa ăn`,
          ...(capture.enabled ? { onClick: () => capture.open('meal', { date: today() }) } : { to: '/day' }) }
      : action?.kind === 'night'
        ? { mood, text: 'Khuya rồi, ngủ ngon nhé!', spoken: 'Bạn Mate: khuya rồi, ngủ ngon nhé. Mở chi tiết hôm nay', to: '/day' }
      : action?.kind === 'meal'
        ? { mood, text: `${NEXT_MEAL_WORD[action.mealType]} nay ăn gì nhỉ?`, spoken: 'Bạn Mate: gợi ý món tiếp theo. Mở thực đơn', to: '/plan' }
        : { mood, text: 'Hôm nay đang ổn nè!', spoken: 'Bạn Mate: hôm nay đang ổn. Mở chi tiết hôm nay', to: '/day' };

  const tiles = {
    log: capture.enabled
      ? { to: '/day', onClick: () => capture.open(), name: 'Ghi lại',
          line: mealDue ? action.title : 'Bữa ăn hoặc tập luyện', spoken: `Ghi lại${mealDue ? `: ${action.title.toLowerCase()}` : ''}` }
      : { to: '/day', name: 'Ghi lại', line: 'Xem nhật ký hôm nay', spoken: 'Xem nhật ký hôm nay' },
    today: !day ? { to: '/day', name: 'Hôm nay', line: '…', spoken: 'Hôm nay: đang tải' } : {
      to: '/day', name: 'Hôm nay',
      line: goals.calories ? `${kcal(eaten)} / ${kcal(goals.calories)} kcal` : `${kcal(eaten)} kcal`,
      spoken: `Hôm nay: đã ăn ${kcal(eaten)}${goals.calories ? ` trên ${kcal(goals.calories)}` : ''} kcal`,
    },
    stats: !weekLoaded ? { to: '/trends', name: 'Thống kê', line: '…', spoken: 'Thống kê: đang tải' } : {
      to: '/trends', name: 'Thống kê', line: `${loggedSoFar}/${daysSoFar.length} ngày`,
      spoken: `Thống kê: tuần này đã ghi ${loggedSoFar} trên ${daysSoFar.length} ngày`,
    },
    plan: plan === undefined ? { to: '/plan', name: 'Thực đơn', line: '…', spoken: 'Thực đơn: đang tải' } : nextPlanned
      ? { to: '/plan', name: 'Thực đơn', line: nextPlanned.name.length > 22 ? `${nextPlanned.name.slice(0, 21)}…` : nextPlanned.name,
          spoken: `Thực đơn: ${suggestFor} hôm nay là ${nextPlanned.name}` }
      : { to: '/plan', name: 'Thực đơn', line: plan ? `Gợi ý ${suggestFor}` : 'Lên thực đơn tuần', spoken: `Thực đơn: ${plan ? `gợi ý ${suggestFor}` : 'lên thực đơn tuần'}` },
  };

  return (
    <div className="flex flex-col gap-4">
      <p className="text-[15px] text-muted">{greeting()} · {longDate(today())}</p>
      <div className="pb-2 pt-10">
        <Hub tiles={tiles} platePct={goals.calories ? (eaten / goals.calories) * 100 : 0} week={week} weekLoaded={weekLoaded}
          mascot={mascot}
          mealDue={mealDue && capture.enabled} />
      </div>
      <AnnouncementBanner screen="home" />
      {!day && <HomeCardsSkeleton />}
      {action && <NextCard action={action} planned={duePlanned} />}
      {day && <TodayCard day={day} goals={goals} />}
    </div>
  );
}
